/* global globalThis */
import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { ChevronDown } from "lucide-react";
import { setUserSelectedOrgConfig } from "../../../header/Orgconfigservice";
import chatbot from "../../../../assets/chatbot.png";
import network from "../../../../assets/network1.png";
import history from "../../../../assets/download2.png";
import download from "../../../../assets/download3.png";

const TableHeader = ({
  globalFilter,
  setGlobalFilter,
  dropdownValue,
  setDropdownValue,
  uploadedFileList,
  customSelectStyles,
  columnVisibilityMenu,
  toggleColumnVisibilityMenu,
  visibilityButtonRef,
  table,
  areAllColumnsSelected,
  selectAllColumns,
  unselectAllColumns,
  showGroupDropdown,
  setShowGroupDropdown,
  dropdownRef,
  typeOfColumns,
  hideColumnsData,
  handleHideColumnsData,
  showInscopeOnly,
  setShowInscopeOnly,
  showMandatoryOnly,
  setShowMandatoryOnly,
  showAIAgent,
  setShowAIAgent,
  setShowReverseEngineering,
  showReverseEngineering,
  navigate,
  pageData,
  fetchMappingDataByExcelID,
}) => {
  React.useEffect(() => {
    // Store UserMappingFileId when component mounts or dropdownValue changes
    if (dropdownValue?.value) {
      const currentStoredId = localStorage.getItem('UserMappingFileId');

      // Only update if it's different to avoid unnecessary storage updates
      if (currentStoredId !== dropdownValue.value) {
        localStorage.setItem('UserMappingFileId', dropdownValue.value);

        // Dispatch custom event for other components to listen
        const event = new CustomEvent('customStorageChange', {
          detail: { key: 'UserMappingFileId', newValue: dropdownValue.value }
        });
        globalThis.dispatchEvent(event);
        setUserSelectedOrgConfig();
      }
    }
  }, [dropdownValue]);

  const [localSearch, setLocalSearch] = React.useState(globalFilter ?? '');


  // Keep in sync if globalFilter changes externally (e.g. restored from API):
const isExternalUpdate = React.useRef(false);

React.useEffect(() => {
  // Only sync inward when globalFilter changes from outside (e.g. restored from API)
  // and the local input doesn't already match
  if (globalFilter !== localSearch) {
    isExternalUpdate.current = true;
    setLocalSearch(globalFilter ?? '');
  }
}, [globalFilter]);

React.useEffect(() => {
  // Skip the debounced push-up if this change was triggered by an external sync
  if (isExternalUpdate.current) {
    isExternalUpdate.current = false;
    return;
  }
  const timer = setTimeout(() => {
    setGlobalFilter(localSearch);
  }, 300);
  return () => clearTimeout(timer);
}, [localSearch]);


  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "10px",
        padding: "8px 10px",
        backgroundColor: "#fafafa",
        borderBottom: "1px solid #e0e0e0",
        flexShrink: 0,
        zIndex: 200,
      }}
    >
      {/* ROW 1: Search + File Dropdown */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {/* Global Search */}
<input
  type="text"
  placeholder="Global Search..."
  value={localSearch}
  onChange={(e) => setLocalSearch(e.target.value)}
  style={{ padding: "4px 8px", width: "200px", height: "28px", flexShrink: 0 }}
/>


        {/* File Dropdown */}
        <Select
          value={dropdownValue}
          onChange={(selectedOption) => {
            setDropdownValue(selectedOption);

            // Store UserMappingFileId in localStorage
            if (selectedOption?.value) {
              localStorage.setItem('UserMappingFileId', selectedOption.value);

              // Dispatch custom event for other components to listen
              const event = new CustomEvent('customStorageChange', {
                detail: { key: 'UserMappingFileId', newValue: selectedOption.value }
              });
              globalThis.dispatchEvent(event);
              setUserSelectedOrgConfig();
            } else {
              // Handle when user clears the selection
              localStorage.removeItem('UserMappingFileId');
            }
          }}
          options={uploadedFileList}
          isSearchable
          placeholder="Select a file"
          menuPortalTarget={document.body}
          styles={{
            ...customSelectStyles,
            control: (provided) => ({
              ...provided,
              width: "300px",
              minHeight: "28px",
              height: "28px",
              flexShrink: 0,
            }),
            menu: (provided) => ({ ...provided, maxHeight: "200px" }),
            menuList: (provided) => ({
              ...provided,
              maxHeight: "200px",
              overflowY: "auto",
            }),
          }}
        />
      </div>

      {/* ROW 2: Visibility Controls + Toggles + Icons */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        {/* Hide Columns Dropdown */}
        <div ref={visibilityButtonRef} style={{ position: "relative", flexShrink: 0 }}>
          <button
            onClick={toggleColumnVisibilityMenu}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "2px 12px 2px 8px",
              backgroundColor: "#fff",
              border: "1px solid #bdbdbd",
              borderRadius: "4px",
              cursor: "pointer",
              height: "28px",
              fontSize: "13px",
              minWidth: "120px",
              boxShadow: columnVisibilityMenu ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.borderColor = "#2196f3")}
            onFocus={(e) => (e.currentTarget.style.borderColor = "#2196f3")}
            onMouseOut={(e) => {
              const borderColor = columnVisibilityMenu ? "#2196f3" : "#bdbdbd";
              e.currentTarget.style.borderColor = borderColor;
            }}
            onBlur={(e) => {
              const borderColor = columnVisibilityMenu ? "#2196f3" : "#bdbdbd";
              e.currentTarget.style.borderColor = borderColor;
            }}
          >
            <span style={{ flex: 1, textAlign: "left" }}>Hide Columns</span>
            <ChevronDown size={14} />
          </button>

          {columnVisibilityMenu && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                width: "200px",
                backgroundColor: "white",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                zIndex: 9999,
                padding: "10px",
                maxHeight: "300px",
                overflowY: "auto",
                marginTop: "2px",
              }}
            >
              {/* Select/Unselect All Button */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "10px",
                  borderBottom: "1px solid #eee",
                  paddingBottom: "8px",
                }}
              >
                {areAllColumnsSelected() ? (
                  <button
                    onClick={unselectAllColumns}
                    style={{
                      padding: "2px 8px",
                      backgroundColor: "#f0f0f0",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "11px",
                      width: "100%",
                    }}
                  >
                    Unselect All
                  </button>
                ) : (
                  <button
                    onClick={selectAllColumns}
                    style={{
                      padding: "2px 8px",
                      backgroundColor: "#f0f0f0",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "11px",
                      width: "100%",
                    }}
                  >
                    Select All
                  </button>
                )}
              </div>

              {/* Column List */}
              {table
                .getAllLeafColumns()
                .filter(
                  (column) =>
                    ![
                      "mapping_file_details_id",
                      "mapping_file_id",
                      "templateid",
                      "isactive",
                    ].includes(column.id)
                )
                .map((column) => (
                  <div
                    key={column.id}
                    style={{
                      margin: "4px 0",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={column.getIsVisible()}
                      onChange={column.getToggleVisibilityHandler()}
                      id={`col-${column.id}`}
                    />
                    <label
                      htmlFor={`col-${column.id}`}
                      style={{
                        marginLeft: "8px",
                        cursor: "pointer",
                        userSelect: "none",
                        fontSize: "12px",
                      }}
                    >
                      {column.id}
                    </label>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Hide Groups Dropdown */}
        <div ref={dropdownRef} style={{ position: "relative", flexShrink: 0 }}>
          <button
            type="button"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "2px 12px 2px 8px",
              backgroundColor: "#fff",
              border: showGroupDropdown ? "1px solid #2196f3" : "1px solid #bdbdbd",
              borderRadius: "4px",
              cursor: "pointer",
              height: "28px",
              fontSize: "13px",
              minWidth: "120px",
              boxShadow: showGroupDropdown ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.borderColor = "#2196f3")}
            onFocus={(e) => (e.currentTarget.style.borderColor = "#2196f3")}
            onMouseOut={(e) => {
              const borderColor = showGroupDropdown ? "#2196f3" : "#bdbdbd";
              e.currentTarget.style.borderColor = borderColor;
            }}
            onBlur={(e) => {
              const borderColor = showGroupDropdown ? "#2196f3" : "#bdbdbd";
              e.currentTarget.style.borderColor = borderColor;
            }}
            onClick={() => setShowGroupDropdown((prev) => !prev)}
          >
            <span style={{ flex: 1, textAlign: "left" }}>Hide Groups</span>
            <ChevronDown size={14} />
          </button>
          {showGroupDropdown && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                width: "200px",
                backgroundColor: "white",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                zIndex: 9999,
                padding: "10px 5px",
                maxHeight: "300px",
                overflowY: "auto",
                marginTop: "2px",
              }}
            >
              {typeOfColumns.map((column) => {
                const lower = column.toLowerCase();
                return (
                  <div
                    className="hide-columns-container"
                    key={column}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "6px 0",
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`group-${column}`}
                      name={column}
                      className="checkox-style"
                      checked={hideColumnsData.includes(lower)}
                      onChange={() => handleHideColumnsData(lower)}
                      style={{ marginRight: 6 }}
                    />
                    <label
                      htmlFor={`group-${column}`}
                      className="ml-1 text-xs"
                      style={{ fontSize: 12, cursor: "pointer" }}
                    >
                      {column} Columns
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Show inscope fields toggle */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexShrink: 0,
          }}
        >
          <input
            type="checkbox"
            id="show-inscope-toggle"
            checked={showInscopeOnly}
            onChange={() => setShowInscopeOnly((prev) => !prev)}
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          <label
            htmlFor="show-inscope-toggle"
            style={{
              fontSize: "12px",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            Inscope
          </label>
        </div>

        {/* Show mandatory fields toggle */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexShrink: 0,
          }}
        >
          <input
            type="checkbox"
            id="show-mandatory-toggle"
            checked={showMandatoryOnly}
            onChange={() => setShowMandatoryOnly((prev) => !prev)}
            style={{ width: "16px", height: "16px", cursor: "pointer" }}
          />
          <label
            htmlFor="show-mandatory-toggle"
            style={{
              fontSize: "12px",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            Mandatory
          </label>
        </div>

        {/* SPACER */}
        <div style={{ flex: 1 }}></div>

        {/* Icons Container */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
            zIndex: 11,
          }}
        >
          {/* AI Agent Icon */}
          <button
            type="button"
            title={showAIAgent ? "Close Mapping Assistant" : "Mapping Assistant"}
            onClick={() => {
              setShowAIAgent(!showAIAgent);
              if (!showAIAgent) setShowReverseEngineering(false);
            }}
            style={{
              cursor: "pointer",
              padding: "6px 8px",
              borderRadius: "4px",
              transition: "all 0.2s ease",
              backgroundColor: showAIAgent ? "#e3f2fd" : "transparent",
              border: showAIAgent ? "1px solid #2196f3" : "1px solid transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              background: showAIAgent ? "#e3f2fd" : "transparent",
            }}
            onMouseOver={(e) => {
              if (!showAIAgent) e.currentTarget.style.backgroundColor = "#f0f0f0";
            }}
            onFocus={(e) => {
              if (!showAIAgent) e.currentTarget.style.backgroundColor = "#f0f0f0";
            }}
            onMouseOut={(e) => {
              if (!showAIAgent) e.currentTarget.style.backgroundColor = "transparent";
            }}
            onBlur={(e) => {
              if (!showAIAgent) e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <img src={chatbot} alt="AI Assistant" style={{ height: "20px" }} />
          </button>

          {/* ERD Icon */}
          <button
            type="button"
            title={showReverseEngineering ? "Close ERD" : "Mapping Graph"}
            onClick={() => {
              setShowReverseEngineering(!showReverseEngineering);
              if (!showReverseEngineering) setShowAIAgent(false);
            }}
            style={{
              cursor: "pointer",
              padding: "6px 8px",
              borderRadius: "4px",
              transition: "all 0.2s ease",
              backgroundColor: showReverseEngineering ? "#e3f2fd" : "transparent",
              border: showReverseEngineering ? "1px solid #2196f3" : "1px solid transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              background: showReverseEngineering ? "#e3f2fd" : "transparent",
            }}
            onMouseOver={(e) => {
              if (!showReverseEngineering) e.currentTarget.style.backgroundColor = "#f0f0f0";
            }}
            onFocus={(e) => {
              if (!showReverseEngineering) e.currentTarget.style.backgroundColor = "#f0f0f0";
            }}
            onMouseOut={(e) => {
              if (!showReverseEngineering) e.currentTarget.style.backgroundColor = "transparent";
            }}
            onBlur={(e) => {
              if (!showReverseEngineering) e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <img src={network} alt="Mapping Graph" style={{ height: "25px" }} />
          </button>

          {/* History Icon */}
          <button
            type="button"
            title="View History"
            onClick={() => navigate(`/history-data/${pageData.fileID}`)}
            style={{
              cursor: "pointer",
              padding: "6px 8px",
              borderRadius: "4px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              background: "transparent",
              border: "none",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
            onFocus={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            onBlur={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <img src={history} alt="View History" style={{ height: "25px" }} />
          </button>

          {/* Export CSV Icon */}
          <button
            type="button"
            title="Export as CSV"
            onClick={() => fetchMappingDataByExcelID(pageData.fileID, true)}
            style={{
              cursor: "pointer",
              padding: "6px 8px",
              borderRadius: "4px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              background: "transparent",
              border: "none",
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
            onFocus={(e) => (e.currentTarget.style.backgroundColor = "#f0f0f0")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            onBlur={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <img src={download} alt="Export as CSV" style={{ height: "25px" }} />
          </button>
        </div>
      </div>
    </div>
  );
};

TableHeader.propTypes = {
  globalFilter: PropTypes.string,
  setGlobalFilter: PropTypes.func,
  dropdownValue: PropTypes.any,
  setDropdownValue: PropTypes.func,
  uploadedFileList: PropTypes.array,
  customSelectStyles: PropTypes.object,
  columnVisibilityMenu: PropTypes.bool,
  toggleColumnVisibilityMenu: PropTypes.func,
  visibilityButtonRef: PropTypes.any,
  table: PropTypes.object,
  areAllColumnsSelected: PropTypes.func,
  selectAllColumns: PropTypes.func,
  unselectAllColumns: PropTypes.func,
  showGroupDropdown: PropTypes.bool,
  setShowGroupDropdown: PropTypes.func,
  dropdownRef: PropTypes.any,
  typeOfColumns: PropTypes.array,
  hideColumnsData: PropTypes.array.isRequired,
  handleHideColumnsData: PropTypes.func,
  showInscopeOnly: PropTypes.bool,
  setShowInscopeOnly: PropTypes.func,
  showMandatoryOnly: PropTypes.bool,
  setShowMandatoryOnly: PropTypes.func,
  showAIAgent: PropTypes.bool,
  setShowAIAgent: PropTypes.func,
  setShowReverseEngineering: PropTypes.func,
  showReverseEngineering: PropTypes.bool,
  navigate: PropTypes.func,
  pageData: PropTypes.object,
  fetchMappingDataByExcelID: PropTypes.func,
};

export default TableHeader;